import type { HTMLProps } from 'react';
import { useMemo } from 'react';

import { ChevronDown } from '@remarkable/ark-icons';
import clsx from 'clsx';

import { CA_PROVINCES, US_STATES } from 'src/utils/countryList';

import { cn } from '../utils/classNamesHelper';

interface StatePickerProps {
  country?: string;
  hasError?: boolean;
}

export function StatePicker2({
  country,
  value,
  hasError,
  disabled,
  ref,
  ...props
}: HTMLProps<HTMLSelectElement> & StatePickerProps) {
  const isUS = country === 'US';
  const isCanada = country === 'CA';

  const states = useMemo(() => {
    if (isUS) return US_STATES;

    if (isCanada) return CA_PROVINCES;

    return [];
  }, [isUS, isCanada]);

  return (
    <div className={cn('rounded-sm relative block')}>
      <select
        data-cy={
          isCanada
            ? 'state-picker-canada'
            : isUS
              ? 'state-picker-us'
              : 'state-picker'
        }
        className={clsx(
          'rounded-sm h-48 w-full cursor-pointer appearance-none border bg-none px-16 py-8',
          {
            'cursor-wait': disabled,
            'shadow-border-muted': !hasError,
            'border-feedback-red-500': hasError,
          }
        )}
        disabled={disabled}
        value={value}
        ref={ref}
        {...props}
      >
        {!value && (
          <option value="">
            {isCanada ? 'Choose province...' : 'Choose state...'}
          </option>
        )}

        {states.map((option) => (
          <option
            key={option.value}
            value={option.value}
            aria-selected={value === option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={24}
        className="pointer-events-none absolute top-0 right-12 h-full text-default"
      />
    </div>
  );
}
